import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { analytics } from 'core/analytics';

import DocumentHead from 'legacyComponents/documentHead';
import { SocialStats } from 'legacyComponents/layout/socialStats';

import { Link } from 'core/routing';

import FormActionButton from 'libs/forms/views/formActionButton';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { TextAreaField } from 'libs/forms/views/fieldTypes/textArea';
import { FileField } from 'libs/forms/views/fieldTypes/file';

import { Cropper } from 'apps/components/cropper';

import { clientStore } from 'apps/client/app';
import { scrollHash } from 'apps/utils';
import { Follow } from 'apps/content/views/follow';

import Button from 'components/Button';
import Tooltip from 'components/Tooltip';

import {
  EditButton,
  ProfileAvatar,
  AvatarInputWrapper,
  NameInput,
  DescriptionInput,
  Summary,
  SettingsButtons,
  Fullname,
  Username,
  Bio,
  ProfileInfo,
} from 'apps/profile/views/overview.styles';

@withNamespaces()
@observer
export class ProfileOverview extends React.Component {
  componentDidMount = () => {
    scrollHash();
  };

  render() {
    const { profileManager } = this.props;
    const { profile } = profileManager;
    const { user, fullName, bio } = profile;
    const { canUpdate, editing, editFormStore } = profile;

    return (
      <>
        <DocumentHead
          ogType='profile'
          ogTypeParams={{
            [`first_name`]: fullName,
            [`username`]: user.username,
          }}
        />
        <ProfileInfo>
          {!editing ? (
            <Summary>
              <ProfileAvatar
                src={profile.avatar?.url}
                name={user.username.substring(1)}
              />
              <Fullname>{fullName}</Fullname>
              <Username>{user.username}</Username>
              <Bio>{bio}</Bio>
            </Summary>
          ) : (
            <Summary>
              <FileField
                removeBorder
                removeOpacity
                editComponent={AvatarInputWrapper}
                previewComponent={Cropper}
                fieldStore={
                  editFormStore && editFormStore.fieldStores.get('avatar')
                }
              />
              <TextField
                fieldStore={
                  editFormStore && editFormStore.fieldStores.get('fullName')
                }
                editComponent={NameInput}
                validationPosition={{ top: '5px', right: '-25px' }}
              />
              <Username>{user.username}</Username>
              <TextAreaField
                fieldStore={
                  editFormStore && editFormStore.fieldStores.get('bio')
                }
                editComponent={DescriptionInput}
                validationPosition={{ top: '2px', right: '-25px' }}
                className='lightBorder'
              />
            </Summary>
          )}
          <SocialStats app={profile} />
          {!canUpdate && (
            <SettingsButtons>
              <Follow content={profile.content} size='small' />
            </SettingsButtons>
          )}
          {canUpdate && !editing && (
            <SettingsButtons>
              <Tooltip id='tooltipSettings' place='right' delayShow={1000} />
              <EditButton text={<Trans>Edit</Trans>} onClick={profile.Edit} />
              <Link
                onClick={() => {
                  analytics.sendEvent(
                    'clicked-settings-profile',
                    {},
                    { ga: true },
                  );
                }}
                name='workspace.spaceSettings.account'
                params={{ spacePath: profile.username }}
              >
                <Button
                  icon='settings'
                  ariaLabel='Personal settings'
                  data-tip='Personal settings'
                  data-for='tooltipSettings'
                  outline
                />
              </Link>
            </SettingsButtons>
          )}
          {clientStore.isStaff && (
            <SettingsButtons>
              <Button
                icon='lightning'
                outline={!profile.managementData.banned}
                variant='warning'
                onClick={
                  profile.managementData.banned
                    ? profileManager.Unban
                    : profileManager.Ban
                }
              />
            </SettingsButtons>
          )}

          {canUpdate && editing && (
            <SettingsButtons>
              <FormActionButton
                formStore={profile.editFormStore}
                buttonHandler={profileManager.Cancel}
                buttonText='Cancel'
                variant='secondary'
                loading={false}
                active={false}
                outline
              />
              <FormActionButton
                formStore={profile.editFormStore}
                buttonHandler={profileManager.Save}
                buttonText='Save'
                buttonTextPosting='Saving...'
              />
            </SettingsButtons>
          )}
        </ProfileInfo>
      </>
    );
  }
}

ProfileOverview.propTypes = {
  profileManager: PropTypes.object,
  editing: PropTypes.bool,
};
